import { customElement, query } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_EXCLUSIONS_GRID } from '../../../../../../../../webcomponents.constants';
import { GzPolicyUiSettingsGridWebComponent } from '../../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/gridView';
import {
  Column,
  ColumnKeys,
  FILTERS,
  getGridColumnDetails,
  GRID_COLUMNS,
  modulesCellCustomRenderer,
  PREDEFINED_FILTERS_KEYS,
} from './grid-config';
import { GzAntimalwareExclusionsGridDataProvider } from './GzAntimalwareExclusionsGridDataProvider';
import { html, TemplateResult } from 'lit';
import { ValidationResult } from '../../../../../../../../ui/gzForm/formField/types/form-validator';
import { getPathValidatorsByType, validateDuplicates } from './utils/validators';
import { GRID_ITEM_FORM_ID } from '../../../../../../../common/policyPanel/uiSettings/gridTemplate/gridComponents/base/component/GzPolicyUiSettingsBaseGrid';
import { GridCellEditWebComponent } from '../../../../../../../../grid/uiElements/gridCellEdit';
import {
  AntimalwareSettingsExclusionsItem,
  AntimalwareSettingsExclusionsModuleType,
  AntimalwareSettingsExclusionsTaskType,
} from '@nirvana/shared/dtos/types/policies/common';
import { MaxLengthValidator } from '../../../../../../../../ui/gzForm/validators/max-length.validator';
import { translateFn } from '../../../../../../../../utils/translator.helper';
import { GzButtonWebComponent } from '../../../../../../../../ui/gzButton';
import { InfoBoxSeverity } from '../../../../../../../../ui/gzInfoBox/types';
import { GzInfoBoxWebComponent } from '../../../../../../../../ui/gzInfoBox';
import { toggleVisibility } from '../../../../../../../../utils/htmlElement.utils';
import { Feature } from '@nirvana/shared/constants';
import {
  getAntimalwareSettingsExclusionsModuleLabelByType,
  getRestrictedAntimalwareSettingsExclusionsTaskTypeFormOptions,
} from '../../../../../utils/uiSettings/antimalware/antimalware.values';
import { MAX_LENGTH_REMARKS } from './utils/validators';
import { ButtonColors, ButtonTypes } from '../../../../../../../../ui/gzButton/types';
import { selectPoliciesState } from '../../../../../redux/slice';
import { store } from '@nirvana/redux/store';
import { GzDialog } from '../../../../../../../../ui/gzDialog/component/GzDialog';
import { GzDialogWebComponent } from '../../../../../../../../ui/gzDialog';
import {
  AntimalwareApiClients,
  ImportFileApiClients,
  PolicyDependenciesContainer,
} from '../../../../../../../common/policyPanel/uiSettings/dependencies/PolicyDependenciesContainer';
import { GzPolicyUiSettingsImportDialog } from '../../../../../../../common/policyPanel/uiSettings/dialogs/import/GzPolicyUiSettingsImportDialog';
import { getPhantom } from '../../../../../redux/actions';
import { getAvailableModuleOptionsByType } from './utils/configs';
import { convertOldModuleExclusionToNewOne } from './utils/converters';
import { PolicyPanelManager } from '../../../../../PolicyPanelManager';
import { areArraysEqual } from '@nirvana/shared/basic-types/object';

@customElement(WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_EXCLUSIONS_GRID)
export class GzAntimalwareExclusionsGridWebComponent extends GzPolicyUiSettingsGridWebComponent<
  Column,
  ColumnKeys,
  AntimalwareSettingsExclusionsItem,
  AntimalwareSettingsExclusionsItem,
  GzAntimalwareExclusionsGridDataProvider
> {
  protected clients = PolicyDependenciesContainer.getInstance().resolveDependency(
    'Antimalware'
  ) as AntimalwareApiClients;

  protected allColumnDetails = getGridColumnDetails();
  protected columns = GRID_COLUMNS;
  protected columnRenderers = {
    [ColumnKeys.MODULE]: (rowData, columnDetails) =>
      modulesCellCustomRenderer(this.gridComponent.controller, rowData, columnDetails),
  };
  protected showHeaderCustomActionsInsideGrid = true;

  protected hasFilters = true;
  protected filters = FILTERS;
  protected predefinedFiltersKeys = PREDEFINED_FILTERS_KEYS;

  protected hasSelectableRows = true;

  protected translations = {
    editRow: translateFn('Edit exclusion'),
    deleteRow: translateFn('Delete'),
    emptyState: translateFn('No exclusions'),
  };

  @query('#delete-button')
  protected deleteButton: GzButtonWebComponent;
  @query('#export-csv-button')
  protected exportButton: GzButtonWebComponent;
  @query('#wildcards-info-box')
  protected wildcardsInfoBox: GzInfoBoxWebComponent;
  @query('#companion-info-box')
  protected companionInfoBox: GzInfoBoxWebComponent;

  protected getDataProvider(): GzAntimalwareExclusionsGridDataProvider {
    return new GzAntimalwareExclusionsGridDataProvider(this.initialGridItems);
  }

  protected onSelectRows(event: CustomEvent) {
    super.onSelectRows(event);
    this.deleteButton.disabled = !event.detail.selectionStoreData.length;
  }

  protected renderHeaderButtons(): TemplateResult {
    return html`
      <gz-tooltip
        id="tooltip"
        class="tooltip"
        tooltipText="${translateFn('Export exclusions (available only for saved policies)')}"
      >
        <gz-button
          id="export-csv-button"
          type="${ButtonTypes.OUTLINE}"
          color="${ButtonColors.SECONDARY}"
          @click="${this.exportRules}"
          ?disabled="${this.isExportButtonDisabled()}"
          >${translateFn('Export')}</gz-button
        >
      </gz-tooltip>

      <gz-button id="import-csv-button" type="${ButtonTypes.OUTLINE}" @click="${this.importRules}"
        >${translateFn('Import')}</gz-button
      >

      <gz-button
        id="delete-button"
        type="outline"
        disabled
        color="negative"
        @click="${this.onDeleteSelectedItemsWithDialog.bind(
          this,
          translateFn('Delete exclusions?'),
          translateFn('You are about to delete the selected exclusions. This action cannot be undone.')
        )}"
        >${translateFn('Delete')}
      </gz-button>
    `;
  }

  protected renderGridItemForm(): TemplateResult {
    return html` <gz-info-box
        severity="${InfoBoxSeverity.WARNING}"
        id="wildcards-info-box"
        class="hidden"
        style="padding: 0;"
      >
        ${translateFn(
          'Use wildcards cautiously when adding exclusions. Specify only the items you want to exclude from scanning.'
        )}
      </gz-info-box>

      <gz-info-box
        severity="${InfoBoxSeverity.INFORMATIVE}"
        id="companion-info-box"
        class="hidden"
        style="padding: 0; margin-top: 8px;"
      >
        ${translateFn('Only the Advanced Threat Control exclusion types listed below apply to your environment.')}
      </gz-info-box>

      <gz-policy-panel-antimalware-exclusions-item
        id="${GRID_ITEM_FORM_ID}"
      ></gz-policy-panel-antimalware-exclusions-item>`;
  }

  public setExportButtonDisabledState() {
    this.exportButton.disabled = this.isExportButtonDisabled();
  }

  private isExportButtonDisabled() {
    const state = selectPoliciesState(store.getState());

    // is disabled for clone or add new policy
    return getPhantom(state);
  }

  private exportRules() {
    const dialogBody = document.createElement('div');
    dialogBody.innerHTML = `
      <div>
      <p>${translateFn('Antimalware exclusions will be saved locally in CSV format.')}</p>
              <gz-info-box id="export-warning" severity="warning" style="  padding-left: 0px; --info-box-width: 100%">
                ${translateFn('Make sure to save the policy to export the latest rules.')}
              </gz-info-box>
      </div>
    `;

    const footer = document.createElement('div');
    footer.innerHTML = `
      <gz-button id="buttonOne" type="full" color="primary">${translateFn('Export')}</gz-button>
      <gz-button id="buttonTwo" type="outline" color="secondary">${translateFn('Cancel')}</gz-button>
    `;

    const footerBtnOne = footer.querySelector('#buttonOne');
    const footerBtnTwo = footer.querySelector('#buttonTwo');

    const dialogConfigs: GzDialog = {
      headline: translateFn('Export exclusions'),
      bodyContent: dialogBody,
      footerContent: footer,
      isOpen: true,
    };

    const dialog = GzDialogWebComponent.buildDialogElement(
      dialogConfigs,
      document.querySelector('.mainContainer') as HTMLElement
    );

    if (footerBtnOne) {
      footerBtnOne.addEventListener('click', () => {
        this.clients.ExportExclusions(PolicyPanelManager.getInstance().getPolicyId());
        dialog.close();
      });
    }

    if (footerBtnTwo) {
      footerBtnTwo.addEventListener('click', () => {
        dialog.dismiss();
      });
    }
  }

  private importRules() {
    const importFn = async (file: File) => {
      const csvFields = '{"taskType":"int","path":"string","module":"string","remarks":"string"}';
      const importClient = PolicyDependenciesContainer.getInstance().resolveDependency(
        'ImportFile'
      ) as ImportFileApiClients;

      return await importClient.ImportCsv(csvFields, file);
    };

    const dialog = new GzPolicyUiSettingsImportDialog(importFn, this.appendExclusionsItemsToGrid.bind(this), {
      title: translateFn('Import exclusions'),
      fileInput: {
        label: translateFn('Exclusions file'),
      },
      validationMessages: {
        valid: translateFn('Exclusions successfully imported.'),
      },
    });
    dialog.show();
  }

  private async appendExclusionsItemsToGrid(
    exclusionItems: {
      taskType: number;
      path: string;
      module: string;
      remarks: string;
    }[]
  ): Promise<{
    valid: boolean;
    rowWithErrors: number[];
  }> {
    this.gridComponent.propertiesSet = false;

    const exclusionItemsToAppendLength = exclusionItems.length;
    const rowWithErrors: number[] = [];
    let exclusionItem: {
      taskType: number;
      path: string;
      module: string | number[];
      remarks: string;
    };
    let convertedExclusionItem: AntimalwareSettingsExclusionsItem;

    for (let i = 0; i < exclusionItemsToAppendLength; i++) {
      exclusionItem = exclusionItems[i];

      // validate empty fields
      if (!exclusionItem || !exclusionItem.taskType || !exclusionItem.path || !exclusionItem.module) {
        rowWithErrors.push(i + 1);
        continue;
      }

      // validate task type
      const availableTaskTypes = getRestrictedAntimalwareSettingsExclusionsTaskTypeFormOptions().map(
        option => option.value
      );
      if (!availableTaskTypes.includes(exclusionItem.taskType)) {
        rowWithErrors.push(i + 1);
        continue;
      }

      // validate path by type
      const validators = getPathValidatorsByType(exclusionItem.taskType);
      let isPathValid = true;
      for (const validator of validators) {
        const result = await validator(exclusionItem.path);

        if (!result.valid) {
          isPathValid = false;
          break;
        }
      }

      // skip row if path is not valid
      if (!isPathValid) {
        rowWithErrors.push(i + 1);
        continue;
      }

      // convert old exclusion item
      convertedExclusionItem = convertOldModuleExclusionToNewOne(exclusionItem);

      // validate modules
      const availableModules = getAvailableModuleOptionsByType(convertedExclusionItem.taskType);
      if (!convertedExclusionItem.module.every(module => availableModules.includes(module))) {
        rowWithErrors.push(i + 1);
        continue;
      }

      // check for duplicates - error is skipped
      if (
        !validateDuplicates(
          convertedExclusionItem,
          (this.gridComponent.dataProvider as GzAntimalwareExclusionsGridDataProvider).items
        ).valid
      ) {
        continue;
      }

      (this.gridComponent.dataProvider as GzAntimalwareExclusionsGridDataProvider).addItem(convertedExclusionItem);
    }
    this.gridComponent.propertiesSet = true;
    this.onGridItemsChange();

    return {
      valid: !rowWithErrors.length,
      rowWithErrors: rowWithErrors,
    };
  }

  protected validateGridItemBeforeAdd(gridItemFormValue: AntimalwareSettingsExclusionsItem): ValidationResult {
    // check for duplicates
    return validateDuplicates(
      gridItemFormValue,
      (this.gridComponent.dataProvider as GzAntimalwareExclusionsGridDataProvider).items
    );
  }

  protected onEditRowEnabled(event: CustomEvent) {
    // set validators on edited row
    const { rowData } = event.detail;

    this.gridComponent.controller?.setValidatorsOnRowAndColumn(
      rowData.index,
      ColumnKeys.PATH,
      getPathValidatorsByType(rowData.item.taskType as AntimalwareSettingsExclusionsTaskType)
    );
    this.gridComponent.controller?.setValidatorsOnRowAndColumn(rowData.index, ColumnKeys.REMARKS, [
      MaxLengthValidator(
        MAX_LENGTH_REMARKS,
        translateFn('The description must have a maximum of {maxLength} characters.', { maxLength: MAX_LENGTH_REMARKS })
      ),
    ]);
  }

  protected isEditedRowChanged(
    oldItem: AntimalwareSettingsExclusionsItem,
    newItem: AntimalwareSettingsExclusionsItem
  ): boolean {
    return (
      oldItem.path !== newItem.path ||
      !areArraysEqual(oldItem.module, newItem.module) ||
      oldItem.remarks !== newItem.remarks
    );
  }

  protected validateGridItemBeforeEdit(
    oldItem: AntimalwareSettingsExclusionsItem,
    newItem: AntimalwareSettingsExclusionsItem
  ): ValidationResult {
    if (oldItem.path === newItem.path && areArraysEqual(oldItem.module, newItem.module)) {
      return { valid: true };
    }

    // remove old item from the list and check for duplicates
    return validateDuplicates(
      newItem,
      (this.gridComponent.dataProvider as GzAntimalwareExclusionsGridDataProvider).items.filter(
        item => item.path !== oldItem.path || !areArraysEqual(item.module, oldItem.module)
      )
    );
  }

  protected revertGridCellEditValue(
    gridCellEdit: GridCellEditWebComponent,
    oldItem: AntimalwareSettingsExclusionsItem
  ): void {
    if (gridCellEdit.id === ColumnKeys.PATH) {
      gridCellEdit.value = oldItem.path;
    }

    if (gridCellEdit.id === ColumnKeys.MODULE) {
      gridCellEdit.value = oldItem.module
        .map(module => getAntimalwareSettingsExclusionsModuleLabelByType(module))
        .join(', ');
    }

    if (gridCellEdit.id === ColumnKeys.REMARKS) {
      gridCellEdit.value = oldItem.remarks;
    }
  }

  public addExclusionItem(item: AntimalwareSettingsExclusionsItem): void {
    const validationResult = this.validateGridItemBeforeAdd(item);
    if (!validationResult.valid) {
      return;
    }

    // add new item to the grid
    this.addGridItem(item);
    this.onGridItemsChange();
  }

  protected async onGridItemsChange() {
    super.onGridItemsChange();

    // show/hide wildcards info box
    const hasWildcards = (this.gridComponent.dataProvider as GzAntimalwareExclusionsGridDataProvider).items.some(
      item => item.path.includes('*') || item.path.includes('?')
    );
    toggleVisibility(this.wildcardsInfoBox, hasWildcards);

    // show/hide companion info box
    const isCompanionStandalone = Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_COMPANION_STANDALONE]);
    if (!isCompanionStandalone) {
      return;
    }

    const availableTypes = getRestrictedAntimalwareSettingsExclusionsTaskTypeFormOptions().map(option => option.value);
    const availableModules = [AntimalwareSettingsExclusionsModuleType.AVC_IDS];

    const hasUnavailableCompanionItems = (
      this.gridComponent.dataProvider as GzAntimalwareExclusionsGridDataProvider
    ).items.some(
      item => !availableTypes.includes(item.taskType) || item.module.some(module => !availableModules.includes(module))
    );
    toggleVisibility(this.companionInfoBox, hasUnavailableCompanionItems);
  }
}
